import React from 'react';
import { Copy, Check, Settings, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { UseShareDatasetReturn } from './types';
import { ShareToggle } from './ShareToggle';
import { SocialShareButtons } from './SocialShareButtons';
import { ShareSettingsPanel } from './ShareSettingsPanel';
import { DeleteShareConfirm } from './DeleteShareConfirm';

interface MobileShareSheetProps {
  share: UseShareDatasetReturn;
  tableName: string;
  onClose: () => void;
  popoverRef: React.RefObject<HTMLDivElement>;
}

export const MobileShareSheet: React.FC<MobileShareSheetProps> = ({ share, tableName, onClose, popoverRef }) => (
  <>
    <div onClick={onClose} className="fixed inset-0 bg-black/40 z-[199] animate-fadeIn" />
    <div ref={popoverRef} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[20px] z-[200] animate-slideUp max-h-[85vh] overflow-auto">
      <div className="flex justify-center py-3">
        <div className="w-9 h-1 bg-neutral-300 rounded-full" />
      </div>
      <div className="flex items-center justify-between px-5 pb-4">
        <h2 className="text-[17px] font-semibold text-neutral-900">Share Dataset</h2>
        <button type="button" onClick={onClose} className="flex items-center justify-center w-8 h-8 bg-neutral-100 rounded-full hover:bg-neutral-200">
          <X className="w-[18px] h-[18px] text-neutral-500" />
        </button>
      </div>
      <div className="px-5 pb-[calc(32px+env(safe-area-inset-bottom,0px))]">
        <div className="p-4 bg-neutral-50 rounded-xl mb-4">
          <ShareToggle isPublic={share.isPublic} isToggling={share.isToggling} onToggle={share.handleTogglePublic} />
        </div>
        {share.isPublic && share.activeShare && (
          <>
            <Button onClick={share.handleCopyShareUrl} variant="primary" fullWidth className={`mb-4 !bg-neutral-900 hover:!bg-neutral-800 ${share.copiedToken ? '!bg-neutral-700' : ''}`} leftIcon={share.copiedToken ? <Check className="w-[18px] h-[18px]" /> : <Copy className="w-[18px] h-[18px]" />}>
              {share.copiedToken ? 'Copied to clipboard' : 'Copy link'}
            </Button>
            <p className="text-xs font-medium text-neutral-500 mb-2.5 uppercase">Share on</p>
            <div className="mb-4">
              <SocialShareButtons shareUrl={share.shareUrl} title={share.shareSettings.title || tableName} />
            </div>
            <button type="button" onClick={() => share.setShowAdvancedOptions(!share.showAdvancedOptions)} className={`w-full flex items-center justify-between p-4 bg-neutral-50 border border-neutral-200 rounded-xl ${share.showAdvancedOptions ? 'mb-3' : ''}`}>
              <div className="flex items-center gap-2.5">
                <Settings className="w-[18px] h-[18px] text-neutral-500" />
                <span className="text-sm font-medium text-neutral-700">Advanced settings</span>
              </div>
              <span className="text-xs text-neutral-400">{share.showAdvancedOptions ? '▲' : '▼'}</span>
            </button>
            {share.showAdvancedOptions && (
              <ShareSettingsPanel settings={share.shareSettings} onSettingsChange={share.setShareSettings} onSave={share.handleSaveSettings} onDelete={() => share.setShowDeleteConfirm(true)} isSaving={share.isSaving} />
            )}
          </>
        )}
      </div>
    </div>
    <DeleteShareConfirm isOpen={share.showDeleteConfirm} onClose={() => share.setShowDeleteConfirm(false)} onConfirm={share.handleDeleteShare} isDeleting={share.isSaving} />
  </>
);
